import { canOpenReconcile } from '@promanage/core';
import type { IconName } from './Icon';

export interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  ready?: boolean;
  /**
   * Keeps a permanent slot in the phone's bottom bar. Everything else moves into the More
   * sheet — see `MOBILE_OVERFLOW` below.
   */
  primary?: boolean;
  /**
   * What the phone's bottom bar calls it, when the full name will not fit a ~78px tab. The
   * full `label` stays the accessible name, so the shorter word is a fit problem and never a
   * second name for the same place.
   */
  short?: string;
  /**
   * Other paths this item is the home of, for a section whose landing page is `/` and so cannot be
   * matched by prefix. The dashboard's second tab is the one case: without this the rail would
   * light nothing while somebody reads it.
   */
  also?: string[];
  /**
   * A count shown on the item wherever it is drawn. Named, not computed here: this file is the
   * list of places and knows nothing about mail — each surface draws `UnreadMailBadge` for it.
   */
  badge?: 'unread-mail';
  /**
   * Who sees the item, when it is not everybody. A presentation rule — the section's route guards
   * it too, so a typed URL lands where the rail would have sent them.
   */
  requires?: (roles: readonly string[]) => boolean;
}

/** The items of `items` this set of roles is shown. Every surface filters through this one. */
export function visibleNav(items: readonly NavItem[], roles: readonly string[]): NavItem[] {
  return items.filter((item) => item.requires?.(roles) ?? true);
}

/** True when `pathname` is one of an item's `also` paths or under one. Its own `to` is the router's job. */
export function navAlsoActive(item: NavItem, pathname: string): boolean {
  return (item.also ?? []).some((path) => pathname === path || pathname.startsWith(`${path}/`));
}

/**
 * The four record lists sit together, in the order the data depends on itself: an owner is
 * needed before a property, a property before a tenancy, a tenancy before the people on it.
 * That is also the order somebody sets an agency up in.
 *
 * DISBURSEMENTS IS NOT HERE, and that is the one deliberate absence. Paying owners out is a
 * decision about the trust ledger — the same money, the same balances, the same person on the
 * same afternoon — so it is a tab in Financial's strip beside Transactions and Commission rather
 * than claiming a ninth rail slot of its own. The rail is for the places the work STARTS; a
 * payment run starts from the balances you have just looked at.
 *
 * Shared by the desktop rail and the phone's bottom bar, so the product has ONE list of
 * places and neither surface can quietly grow a destination the other does not have.
 */
export const NAV: NavItem[] = [
  {
    to: '/',
    label: 'Dashboard',
    icon: 'dashboard',
    ready: true,
    primary: true,
    also: ['/dashboard'],
  },
  {
    to: '/reconcile',
    label: 'Reconcile',
    icon: 'bank',
    ready: true,
    primary: true,
    requires: canOpenReconcile,
  },
  { to: '/financial', label: 'Financial', icon: 'ledger', ready: true, primary: true },
  { to: '/owners', label: 'Owners', icon: 'owners', ready: true },
  { to: '/properties', label: 'Properties', icon: 'property', ready: true },
  { to: '/tenancies', label: 'Tenancies', icon: 'tenancy', ready: true },
  { to: '/people', label: 'People', icon: 'people', ready: true },
  /*
   * BILLS IS THE SPENT HALF OF THE MONEY — what the agency pays out on an owner's behalf, rather
   * than what it collects for them. It is not under Financial with the ledger screens: a property
   * manager keying an invoice is doing property work, and burying the queue two levels down is how
   * invoices sit unapproved for a fortnight. Nor under Maintenance: rates and insurance are bills
   * with no job behind them.
   */
  { to: '/bills', label: 'Bills', icon: 'invoice', ready: true },
  /*
   * MAINTENANCE IS A QUEUE — the thing a tenant is waiting on — so it comes after the record lists,
   * and the order of the rail puts the record lists first and the work last.
   *
   * NOT UNDER PROPERTIES, which is the other obvious home. A property manager's morning question
   * is "what is outstanding across the whole book", not "what is outstanding at 12 Kowhai St" —
   * the second is a tab on the property, and it is there too.
   */
  { to: '/maintenance', label: 'Maintenance', icon: 'maintenance', ready: true, short: 'Jobs' },
  /*
   * CONTRACTORS IS NOT HERE: it is a tab on Maintenance. It is a file you look somebody up in, not
   * a place work starts, and the time you look one up is when sending them to a job — the reasoning
   * is in `MaintenanceNav`.
   */
  /*
   * INSPECTIONS IS NOT HERE: it is a tab on Properties, because an inspection is of a property
   * and the plan it walks is the property's — the reasoning is in `PropertiesNav`.
   */
  /*
   * COMMUNICATIONS: saved lists of tenancies and owners today, and the bulk message sent to one of
   * them next. Its own place rather than a tab on People because a list reaches across records — a
   * tenancy's property, an owner's holdings — and because where it is going is sending, which is
   * not a record list at all. Before Automations: this is work somebody does, and Automations is
   * where the agency decides what the software does instead.
   */
  /*
   * IT OPENS ON THE INBOX — what has arrived is the part of this that cannot wait, where a list
   * is something somebody goes to build. `also` keeps the item lit on the lists and the sent
   * emails, which live beside the inbox under `/communications` rather than under it. The badge
   * is the unread mail assigned to whoever is looking.
   */
  {
    to: '/communications/inbox',
    label: 'Communications',
    icon: 'mail',
    ready: true,
    short: 'Comms',
    also: ['/communications'],
    badge: 'unread-mail',
  },
  /*
   * AUTOMATIONS SITS BETWEEN THE WORK AND THE REPORTS, and it is the only rail item that is not a
   * place work is DONE — it is where an agency decides what the software does on its behalf.
   *
   * NOT UNDER SETTINGS, which is the obvious home and the wrong one. Settings is where facts about
   * the agency are recorded — its GST registration, its statement schedule — and nobody goes there
   * on a Tuesday morning. An automation is operational: it fires, it can fail, and the question
   * "did the owner get told" is asked with a tenant on the phone. Two levels down beside the tax
   * settings is where rules go to be forgotten, which is the one thing automation cannot afford.
   */
  { to: '/automations', label: 'Automations', icon: 'automation', ready: true, short: 'Auto' },
  { to: '/reports', label: 'Reports', icon: 'report', ready: true },
];

/**
 * A phone gets a handful of destinations and a More tab, not the whole rail.
 *
 * Eight 40px targets across a 390px screen is 48px each with nothing left for a label, which
 * is how the bottom bar ended up as a row of clipped, unlabelled glyphs. A few plus overflow
 * is the width at which a tab can still say what it is.
 *
 * WHICH ones is the money path, not the record lists: on a phone you are checking whether the
 * morning's rent landed and what the trust balance is — not setting up a property. Owners,
 * properties, tenancies, people and reports are work you do at a desk, and they are one tap
 * away in More rather than gone. A payment run is reachable in two, through Financial, which
 * is the right depth for something nobody authorises from a bus stop.
 */
export const MOBILE_PRIMARY: NavItem[] = NAV.filter((item) => item.primary);

/** The rest, shown in the More sheet. Same objects, so nothing can fall between the two. */
export const MOBILE_OVERFLOW: NavItem[] = NAV.filter((item) => !item.primary);
